import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogActions,
  Divider,
} from '@mui/material';

import {
  getEventRuntime,
  startEvent,
  endEvent,
  adjustScore,
  undoScore,
  resetGame,
} from '../../services/event-runtime.service';

type Runtime = {
  eventId: string;
  name: string;
  title?: string;
  baseName?: string;
  gameType: string;
  phase?: 'pre' | 'during' | 'post';
  gamePeriod: number;
  maxGamePeriods: number;
  inPlay: boolean;
  periodStatus?: 'ready' | 'running' | 'ended';
  labels?: {
    currentPeriodName: string;
    nextPeriodName: string;
    startActionLabel: string;
    endActionLabel: string;
    gameOverLabel: string;
    continuous?: boolean;
  };
  score?: {
    left: number;
    right: number;
  };
  timer: {
    startedAt: number | null;
    accumulatedMs: number;
    running: boolean;
  };
  permissions: {
    canControlTimer: boolean;
    canViewTimer: boolean;
  };
  log: { ts: number; action: string; note?: string }[];
};

function toTitleCase(value: string) {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function splitTeamsFromTitle(title: string): { leftTeam: string; rightTeam: string } | null {
  const parts = title.split(/\s+vs\s+/i);

  if (parts.length !== 2) {
    return null;
  }

  return {
    leftTeam: parts[0].trim(),
    rightTeam: parts[1].trim(),
  };
}

function isRuntimeShape(value: any): value is Runtime {
  return !!value &&
    typeof value === 'object' &&
    typeof value.eventId === 'string' &&
    typeof value.name === 'string' &&
    typeof value.gameType === 'string' &&
    typeof value.gamePeriod === 'number' &&
    typeof value.maxGamePeriods === 'number' &&
    typeof value.inPlay === 'boolean' &&
    !!value.timer &&
    typeof value.timer === 'object' &&
    typeof value.timer.accumulatedMs === 'number' &&
    typeof value.timer.running === 'boolean' &&
    !!value.permissions &&
    typeof value.permissions === 'object' &&
    typeof value.permissions.canControlTimer === 'boolean' &&
    typeof value.permissions.canViewTimer === 'boolean' &&
    Array.isArray(value.log);
}

export default function EventServicePage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [runtime, setRuntime] = useState<Runtime | null>(null);
  const [displaySeconds, setDisplaySeconds] = useState(0);
  const [confirm, setConfirm] = useState<'start' | 'end' | 'reset' | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorText, setErrorText] = useState('');
  const [scoreBusy, setScoreBusy] = useState<'left-add' | 'left-sub' | 'right-add' | 'right-sub' | 'undo' | null>(null);

  const logPrefix = useMemo(() => `[event-service id=${String(id || '')}]`, [id]);

  const loadRuntime = useCallback(async (silent = false) => {
    if (!id) {
      console.warn(`${logPrefix} loadRuntime skipped because route param id is missing`);
      setErrorText('Missing event id in route');
      setLoading(false);
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
      }

      const data = await getEventRuntime(id);

      if (!isRuntimeShape(data)) {
        setErrorText('Runtime response shape invalid');
        return;
      }

      setRuntime(data);
      setErrorText('');
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Unable to load event runtime';

      setErrorText(String(message));
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [id, logPrefix]);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setErrorText('Missing event id in route');
      return;
    }

    loadRuntime();

    const interval = setInterval(() => {
      loadRuntime(true);
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [id, loadRuntime]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!runtime) return;

      const { startedAt, accumulatedMs, running } = runtime.timer;

      let total = accumulatedMs;

      if (running && startedAt) {
        total += Date.now() - startedAt;
      }

      setDisplaySeconds(Math.floor(total / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [runtime]);

  useEffect(() => {
    if (!runtime) return;

    const { startedAt, accumulatedMs, running } = runtime.timer;

    let total = accumulatedMs;

    if (running && startedAt) {
      total += Date.now() - startedAt;
    }

    setDisplaySeconds(Math.floor(total / 1000));
  }, [runtime]);

  if (loading && !runtime) {
    return (
      <Box p={2}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!runtime) {
    return (
      <Box p={2}>
        <Typography color="error" sx={{ mb: 2 }}>
          {errorText || 'Runtime is empty on the client'}
        </Typography>

        <Button variant="outlined" onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>
    );
  }

  const minutes = Math.floor(displaySeconds / 60);
  const seconds = displaySeconds % 60;
  const isAdmin = runtime.permissions.canControlTimer;

  const phase =
    runtime.phase ||
    (runtime.gamePeriod === 0 && !runtime.inPlay
      ? 'pre'
      : runtime.gamePeriod >= runtime.maxGamePeriods && !runtime.inPlay
      ? 'post'
      : 'during');

  const phaseLabel = phase === 'pre' ? 'Pre-game' : phase === 'post' ? 'Post-game' : 'During';

  const currentPeriodName = runtime.labels?.currentPeriodName || 'Period';

  const nextPeriodNumber =
    runtime.gamePeriod === 0
      ? 1
      : Math.min(runtime.gamePeriod + 1, runtime.maxGamePeriods);

  const nextPeriodName =
    runtime.labels?.nextPeriodName || `Period ${nextPeriodNumber}`;

  const scoreLeft = runtime.score?.left ?? 0;
  const scoreRight = runtime.score?.right ?? 0;

  const titleBase = runtime.baseName || runtime.name || '';
  const teams = splitTeamsFromTitle(titleBase);
  const leftTeamName = teams?.leftTeam || 'Team A';
  const rightTeamName = teams?.rightTeam || 'Team B';

  const isHalfTime =
    phase === 'during' &&
    !runtime.inPlay &&
    runtime.gamePeriod > 0 &&
    runtime.gamePeriod < runtime.maxGamePeriods;

  const isFullTime = phase === 'post';
  const showScorePanel = phase !== 'pre' && phase !== 'post';

  const scoreTitle = teams
    ? `${leftTeamName} ${scoreLeft} - ${scoreRight} ${rightTeamName}`
    : `${titleBase} ${scoreLeft} - ${scoreRight}`;

  const titleText = runtime.title || (phase === 'pre'
    ? titleBase
    : isHalfTime
    ? `HT: ${scoreTitle}`
    : isFullTime
    ? `FT: ${scoreTitle}`
    : scoreTitle);

  const periodSummary = runtime.labels?.continuous
    ? currentPeriodName
    : `${currentPeriodName}`;

  const primaryAction: 'start' | 'end' | null =
    phase === 'post'
      ? null
      : runtime.inPlay
      ? 'end'
      : 'start';

  const primaryActionLabel =
    primaryAction === 'start'
      ? runtime.labels?.startActionLabel || `Start ${runtime.gamePeriod === 0 ? currentPeriodName : nextPeriodName}`
      : primaryAction === 'end'
      ? runtime.labels?.endActionLabel || 'End Period'
      : runtime.labels?.gameOverLabel || 'Game Over';

  const handleScore = async (
    side: 'left' | 'right',
    delta: number,
    busyKey: 'left-add' | 'left-sub' | 'right-add' | 'right-sub',
  ) => {
    if (!id) return;

    try {
      setScoreBusy(busyKey);
      const data = await adjustScore(id, side, delta);

      if (!isRuntimeShape(data)) {
        setErrorText('Score action returned invalid runtime shape');
        return;
      }

      setRuntime(data);
      setErrorText('');
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Score action failed';
      setErrorText(String(message));
    } finally {
      setScoreBusy(null);
    }
  };

  const handleConfirmedAction = async () => {
    if (!id || !confirm) {
      return;
    }

    try {
      const data =
        confirm === 'start'
          ? await startEvent(id)
          : confirm === 'end'
          ? await endEvent(id)
          : await resetGame(id);

      if (!isRuntimeShape(data)) {
        setErrorText(`${confirm} action returned invalid runtime shape`);
        return;
      }

      setRuntime(data);
      setErrorText('');
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Action failed';

      setErrorText(String(message));
    } finally {
      setConfirm(null);
    }
  };

  const handleUndo = async () => {
    if (!id) return;

    try {
      setScoreBusy('undo');
      const data = await undoScore(id);

      if (!isRuntimeShape(data)) {
        setErrorText('Undo action returned invalid runtime shape');
        return;
      }

      setRuntime(data);
      setErrorText('');
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Undo action failed';
      setErrorText(String(message));
    } finally {
      setScoreBusy(null);
    }
  };

  const confirmTitle =
    confirm === 'start'
      ? `${primaryActionLabel}?`
      : confirm === 'end'
      ? `${primaryActionLabel}?`
      : 'Reset game?';

  const sideNavButtons: Array<{
    key: string;
    label: string;
    active?: boolean;
    onClick?: () => void;
    variant?: 'contained' | 'outlined' | 'text';
    color?: 'primary' | 'inherit' | 'success' | 'error' | 'warning';
    disabled?: boolean;
  }> = [
    ...(isAdmin && primaryAction
      ? [{
          key: 'time',
          label: primaryActionLabel,
          active: runtime.inPlay,
          onClick: () => setConfirm(primaryAction),
          variant: 'contained' as const,
          color: primaryAction === 'start' ? 'success' as const : 'error' as const,
        }]
      : []),
    {
      key: 'score',
      label: showScorePanel ? 'Score live' : 'Score',
      active: showScorePanel,
      variant: 'outlined' as const,
      disabled: true,
    },
    {
      key: 'status',
      label: phaseLabel,
      active: true,
      variant: 'outlined' as const,
      disabled: true,
    },
    {
      key: 'posts',
      label: 'Posts',
      variant: 'outlined' as const,
      disabled: true,
    },
    {
      key: 'back',
      label: 'Back',
      onClick: () => navigate(-1),
      variant: 'text' as const,
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '160px 1fr' },
        gap: 2,
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: '1px solid #ddd',
          bgcolor: '#f5f6f7',
        }}
      >
        <Box display="flex" flexDirection="column" gap={1.5}>
          {sideNavButtons.map((button) => (
            <Button
              key={button.key}
              fullWidth
              variant={button.variant || (button.active ? 'contained' : 'outlined')}
              color={button.color || (button.active ? 'success' : 'inherit')}
              onClick={button.onClick}
              disabled={button.disabled}
              sx={{
                justifyContent: 'center',
                minHeight: 46,
                textTransform: 'none',
                fontWeight: button.active ? 700 : 500,
              }}
            >
              {button.label}
            </Button>
          ))}

          <Paper
            elevation={0}
            sx={{
              mt: 2,
              p: 1,
              border: '1px solid #ddd',
              bgcolor: '#fff',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Live score
            </Typography>

            <Box display="flex" flexDirection="column" gap={1}>
              <Button
                variant="contained"
                onClick={() => handleScore('left', 1, 'left-add')}
                disabled={!isAdmin || scoreBusy !== null || phase === 'pre' || phase === 'post'}
              >
                + {leftTeamName}
              </Button>

              <Button
                variant="contained"
                onClick={() => handleScore('right', 1, 'right-add')}
                disabled={!isAdmin || scoreBusy !== null || phase === 'pre' || phase === 'post'}
              >
                + {rightTeamName}
              </Button>

              <Button
                variant="outlined"
                onClick={handleUndo}
                disabled={!isAdmin || scoreBusy !== null}
              >
                ↶ Undo
              </Button>
            </Box>
          </Paper>
        </Box>
      </Paper>

      <Box display="flex" flexDirection="column" gap={2}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: '1px solid #ccc',
            bgcolor: '#fff',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            {titleText}
          </Typography>

          <Typography variant="body1" color="text.secondary">
            {toTitleCase(runtime.gameType || 'Sport')}
          </Typography>

          <Typography variant="body1" color="text.secondary">
            {periodSummary} · {runtime.inPlay ? 'Live' : runtime.periodStatus || 'Ready'}
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: '1px solid #ddd',
            bgcolor: '#fff',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Annotation panel
          </Typography>

          <Typography color="text.secondary">
            This wider area is ready for pitch-side play annotation buttons and sport-specific event controls.
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: '1px solid #ddd',
            bgcolor: '#fff',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Match clock
          </Typography>

          <Typography variant="h3" sx={{ fontVariantNumeric: 'tabular-nums', mb: 1 }}>
            {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
          </Typography>

          <Typography color="text.secondary">
            {runtime.inPlay ? 'Clock running' : 'Clock stopped'}
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: '1px solid #ddd',
            bgcolor: '#fff',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Scoreboard
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="body1" color="text.secondary">
                {leftTeamName}
              </Typography>
            </Box>

            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {scoreLeft} - {scoreRight}
            </Typography>

            <Box textAlign="right">
              <Typography variant="body1" color="text.secondary">
                {rightTeamName}
              </Typography>
            </Box>
          </Box>

          {isAdmin && showScorePanel && (
            <>
              <Divider sx={{ my: 2 }} />

              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  onClick={() => handleScore('left', -1, 'left-sub')}
                  disabled={scoreBusy !== null || scoreLeft <= 0}
                >
                  - {leftTeamName}
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => handleScore('right', -1, 'right-sub')}
                  disabled={scoreBusy !== null || scoreRight <= 0}
                >
                  - {rightTeamName}
                </Button>
              </Box>
            </>
          )}
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: '1px solid #ddd',
            bgcolor: '#fff',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Recent actions
          </Typography>

          {runtime.log.length ? (
            <Box display="flex" flexDirection="column" gap={1}>
              {runtime.log
                .slice()
                .reverse()
                .map((item, index) => (
                  <Typography key={`${item.ts}-${index}`} variant="body1">
                    {new Date(item.ts).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    })}{' '}
                    — {item.action}
                    {item.note ? ` (${item.note})` : ''}
                  </Typography>
                ))}
            </Box>
          ) : (
            <Typography color="text.secondary">No service actions recorded yet.</Typography>
          )}
        </Paper>

        {errorText ? (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: '1px solid #f3b2b2',
              bgcolor: '#fff5f5',
            }}
          >
            <Typography color="error">{errorText}</Typography>
          </Paper>
        ) : null}
      </Box>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>{confirmTitle}</DialogTitle>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={confirm === 'end' ? 'error' : confirm === 'reset' ? 'warning' : 'success'}
            onClick={handleConfirmedAction}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}